import * as Tone from "tone"
import { AUDIO } from "../core/keys"
import { SfxDebug } from "./sfx-debug"

/**
 * Tone.js SFX synthesis for Bark to the Future.
 *
 * Design principles:
 * - Pre-built synth pools (zero allocation at play time)
 * - Every sound has a pitch sweep or envelope shape that gives it character
 * - Durations sized to fire rates: bark 120ms (350ms cooldown), goo 80ms (120ms cooldown)
 * - Layered synthesis: body + character + texture per sound
 * - Safe wrappers: rapid re-triggers drop gracefully instead of crashing
 */
export class ToneSfxSynthesizer {
  private static readonly pickupPolyphony = 48
  private static readonly rewardPolyphony = 48
  private static readonly waveStartPolyphony = 16

  // ── Mix buses ──────────────────────────────────────────────
  private master: Tone.Gain
  private launcherBus: Tone.Gain
  private impactBus: Tone.Gain
  private uiBus: Tone.Gain
  private ambientBus: Tone.Gain

  // ── Shared effects ────────────────────────────────────────
  private launcherComp: Tone.Compressor
  private impactVerb: Tone.Reverb
  private uiFilter: Tone.Filter

  // ── Synth pool ────────────────────────────────────────────
  // Bark
  private barkVocal!: Tone.FMSynth
  private barkBody!: Tone.MembraneSynth
  // Laser
  private laserSynth!: Tone.MonoSynth
  // Bumper
  private bumperDing!: Tone.MetalSynth
  private bumperThump!: Tone.MembraneSynth
  // Explosion
  private exploBoom!: Tone.NoiseSynth
  private exploKick!: Tone.MembraneSynth
  private exploCrack!: Tone.NoiseSynth
  // Pickup
  private pickupSynth!: Tone.PolySynth
  // Damage
  private damageCrush!: Tone.BitCrusher | Tone.Gain
  private damageNoise!: Tone.NoiseSynth
  private damageThud!: Tone.MembraneSynth
  // Plasma rail
  private railSynth!: Tone.FMSynth
  // Shotgun
  private shotgunPop!: Tone.MembraneSynth
  private shotgunScatter!: Tone.NoiseSynth
  // Heavy cannon
  private cannonBoom!: Tone.MembraneSynth
  private cannonRumble!: Tone.NoiseSynth
  // Hadouken
  private hadoukenCharge!: Tone.MonoSynth
  private hadoukenChargeNoise!: Tone.NoiseSynth
  private hadoukenPulse!: Tone.FMSynth
  private hadoukenSub!: Tone.MembraneSynth
  private hadoukenAir!: Tone.NoiseSynth
  // Fire shot
  private fireShotPulse!: Tone.FMSynth
  private fireShotSub!: Tone.MembraneSynth
  private fireShotAir!: Tone.NoiseSynth
  // Goo
  private gooSynth!: Tone.Synth
  private gooFilter!: Tone.Filter
  // Roar
  private roarSynth!: Tone.AMSynth
  private roarSub!: Tone.Synth
  // Ricochet
  private ricochetSynth!: Tone.Synth
  // Mine
  private mineSynth!: Tone.MetalSynth
  // Beep / UI
  private beepSynth!: Tone.Synth
  private uiClickSynth!: Tone.Synth
  private rewardSynth!: Tone.PolySynth
  private rewardBell!: Tone.MetalSynth
  // Wave start
  private waveStartSynth!: Tone.PolySynth
  private waveStartFilter!: Tone.Filter
  // Warp
  private warpSynth!: Tone.MonoSynth
  private warpNoise!: Tone.NoiseSynth
  // Flyby
  private flybySynth!: Tone.Synth
  private flybyPan!: Tone.Panner
  // Thruster
  private thrusterNoise!: Tone.NoiseSynth
  private thrusterBP!: Tone.Filter
  private ballLaunchSynth!: Tone.MonoSynth
  private ballLaunchNoise!: Tone.NoiseSynth
  private ballStateSynth!: Tone.FMSynth
  private ballExpireNoise!: Tone.NoiseSynth

  private initialized = false

  constructor() {
    // Master at conservative level to prevent clipping
    this.master = new Tone.Gain(0.62).toDestination()
    this.launcherBus = new Tone.Gain(0.58).connect(this.master)
    this.impactBus = new Tone.Gain(0.68).connect(this.master)
    this.uiBus = new Tone.Gain(0.44).connect(this.master)
    this.ambientBus = new Tone.Gain(0.56).connect(this.master)

    // Launcher bus: light compression to tame rapid fire
    this.launcherComp = new Tone.Compressor({
      threshold: -16,
      ratio: 3.5,
      attack: 0.002,
      release: 0.045,
    }).connect(this.launcherBus)
    // Impact bus: short reverb tail for space
    this.impactVerb = new Tone.Reverb({ decay: 0.85, wet: 0.16 }).connect(
      this.impactBus,
    )
    // UI bus: gentle high-shelf, keep brightness
    this.uiFilter = new Tone.Filter(8000, "lowpass").connect(this.uiBus)

    this.buildSynths()
  }

  // ─────────────────────────────────────────────────────────────
  //  SYNTH CONSTRUCTION
  // ─────────────────────────────────────────────────────────────

  private buildSynths(): void {
    // ── BARK ─────────────────────────────────────────────────
    // FM "woof": triangle carrier through square modulator for vocal-cord harmonics.
    // The play method drops pitch rapidly to create the formant sweep of a bark.
    this.barkVocal = new Tone.FMSynth({
      harmonicity: 3,
      modulationIndex: 14,
      oscillator: { type: "triangle" },
      modulation: { type: "square" },
      envelope: { attack: 0.003, decay: 0.09, sustain: 0.01, release: 0.04 },
      modulationEnvelope: {
        attack: 0.003,
        decay: 0.07,
        sustain: 0,
        release: 0.02,
      },
    }).connect(this.launcherComp)

    this.barkBody = new Tone.MembraneSynth({
      pitchDecay: 0.04,
      octaves: 4,
      oscillator: { type: "sine" },
      envelope: { attack: 0.001, decay: 0.1, sustain: 0, release: 0.03 },
    }).connect(this.launcherComp)

    // ── LASER ────────────────────────────────────────────────
    // Classic "pew": sawtooth with descending filter + pitch sweep.
    this.laserSynth = new Tone.MonoSynth({
      oscillator: { type: "sawtooth" },
      envelope: { attack: 0.001, decay: 0.13, sustain: 0, release: 0.02 },
      filterEnvelope: {
        attack: 0.001,
        decay: 0.11,
        sustain: 0,
        release: 0.02,
        baseFrequency: 800,
        octaves: 4,
      },
    }).connect(this.launcherComp)

    // ── BUMPER ───────────────────────────────────────────────
    // Pinball: bright metallic ding + satisfying sub thump.
    this.bumperDing = new Tone.MetalSynth({
      envelope: { attack: 0.001, decay: 0.18, release: 0.08 },
      harmonicity: 5.1,
      modulationIndex: 12,
      resonance: 4000,
      octaves: 1.5,
    }).connect(this.impactVerb)

    this.bumperThump = new Tone.MembraneSynth({
      pitchDecay: 0.05,
      octaves: 6,
      oscillator: { type: "sine" },
      envelope: { attack: 0.001, decay: 0.12, sustain: 0, release: 0.04 },
    }).connect(this.impactBus)

    // ── EXPLOSION ────────────────────────────────────────────
    // Three layers: brown noise body + sub kick + white noise crack.
    this.exploBoom = new Tone.NoiseSynth({
      noise: { type: "brown" },
      envelope: { attack: 0.002, decay: 0.4, sustain: 0, release: 0.15 },
    }).connect(this.impactVerb)

    this.exploKick = new Tone.MembraneSynth({
      pitchDecay: 0.1,
      octaves: 7,
      oscillator: { type: "sine" },
      envelope: { attack: 0.001, decay: 0.35, sustain: 0, release: 0.1 },
    }).connect(this.impactBus)

    this.exploCrack = new Tone.NoiseSynth({
      noise: { type: "white" },
      envelope: { attack: 0.001, decay: 0.06, sustain: 0, release: 0.01 },
    }).connect(this.impactBus)

    // ── PICKUP ───────────────────────────────────────────────
    // Bright ascending arpeggio: triangle waves with body.
    this.pickupSynth = new Tone.PolySynth({
      voice: Tone.Synth,
      maxPolyphony: ToneSfxSynthesizer.pickupPolyphony,
      options: {
        oscillator: { type: "triangle" },
        envelope: {
          attack: 0.003,
          decay: 0.15,
          sustain: 0.05,
          release: 0.08,
        },
      },
    }).connect(this.uiBus)

    // ── DAMAGE ───────────────────────────────────────────────
    // Crunchy impact: bit-crushed noise + low membrane thud.
    // BitCrusher requires AudioWorklet which needs a secure context (HTTPS/localhost)
    // and is unavailable in iOS WKWebView. Check before constructing to avoid an
    // unhandled async rejection (the failure is async, so try/catch won't catch it).
    if (window.isSecureContext && typeof AudioWorklet !== "undefined") {
      this.damageCrush = new Tone.BitCrusher(4).connect(this.impactBus)
    } else {
      this.damageCrush = new Tone.Gain(1).connect(this.impactBus)
    }
    this.damageNoise = new Tone.NoiseSynth({
      noise: { type: "white" },
      envelope: { attack: 0.001, decay: 0.1, sustain: 0, release: 0.02 },
    }).connect(this.damageCrush)

    this.damageThud = new Tone.MembraneSynth({
      pitchDecay: 0.06,
      octaves: 5,
      oscillator: { type: "sine" },
      envelope: { attack: 0.001, decay: 0.12, sustain: 0, release: 0.03 },
    }).connect(this.impactBus)

    // ── PLASMA RAIL ──────────────────────────────────────────
    // High-energy zap: FM with sine carrier, rising mod index gives "charging" feel.
    this.railSynth = new Tone.FMSynth({
      harmonicity: 5,
      modulationIndex: 18,
      oscillator: { type: "sine" },
      modulation: { type: "triangle" },
      envelope: { attack: 0.002, decay: 0.18, sustain: 0, release: 0.04 },
      modulationEnvelope: {
        attack: 0.01,
        decay: 0.16,
        sustain: 0,
        release: 0.03,
      },
    }).connect(this.launcherComp)

    // ── SHOTGUN ──────────────────────────────────────────────
    // Wide blast: low pop + white noise scatter.
    this.shotgunPop = new Tone.MembraneSynth({
      pitchDecay: 0.02,
      octaves: 4,
      oscillator: { type: "sine" },
      envelope: { attack: 0.001, decay: 0.08, sustain: 0, release: 0.02 },
    }).connect(this.launcherComp)

    this.shotgunScatter = new Tone.NoiseSynth({
      noise: { type: "white" },
      envelope: { attack: 0.001, decay: 0.14, sustain: 0, release: 0.04 },
    }).connect(this.launcherComp)

    // ── HEAVY CANNON ─────────────────────────────────────────
    // Deep boom: massive pitch sweep + brown noise rumble.
    this.cannonBoom = new Tone.MembraneSynth({
      pitchDecay: 0.15,
      octaves: 8,
      oscillator: { type: "sine" },
      envelope: { attack: 0.001, decay: 0.35, sustain: 0, release: 0.08 },
    }).connect(this.launcherComp)

    this.cannonRumble = new Tone.NoiseSynth({
      noise: { type: "brown" },
      envelope: { attack: 0.005, decay: 0.2, sustain: 0, release: 0.05 },
    }).connect(this.launcherComp)

    // ── HADOUKEN ─────────────────────────────────────────────
    // Charge + triple energy shove: warm harmonics, breath, and sub punches.
    this.hadoukenCharge = new Tone.MonoSynth({
      oscillator: { type: "sawtooth" },
      envelope: { attack: 0.18, decay: 0.7, sustain: 0.18, release: 0.08 },
      filterEnvelope: {
        attack: 0.2,
        decay: 0.75,
        sustain: 0,
        release: 0.05,
        baseFrequency: 180,
        octaves: 4.2,
      },
    }).connect(this.launcherComp)

    this.hadoukenChargeNoise = new Tone.NoiseSynth({
      noise: { type: "pink" },
      envelope: { attack: 0.16, decay: 0.85, sustain: 0, release: 0.08 },
    }).connect(this.launcherComp)

    this.hadoukenPulse = new Tone.FMSynth({
      harmonicity: 1.8,
      modulationIndex: 9,
      oscillator: { type: "triangle" },
      modulation: { type: "square" },
      envelope: { attack: 0.002, decay: 0.16, sustain: 0, release: 0.04 },
      modulationEnvelope: {
        attack: 0.001,
        decay: 0.1,
        sustain: 0,
        release: 0.02,
      },
    }).connect(this.launcherComp)

    this.hadoukenSub = new Tone.MembraneSynth({
      pitchDecay: 0.05,
      octaves: 6,
      oscillator: { type: "sine" },
      envelope: { attack: 0.001, decay: 0.16, sustain: 0, release: 0.04 },
    }).connect(this.impactBus)

    this.hadoukenAir = new Tone.NoiseSynth({
      noise: { type: "white" },
      envelope: { attack: 0.001, decay: 0.12, sustain: 0, release: 0.03 },
    }).connect(this.launcherComp)

    // ── FIRE SHOT ────────────────────────────────────────────
    // Smaller sibling of the hadouken release for regular yellow fire shots.
    this.fireShotPulse = new Tone.FMSynth({
      harmonicity: 1.55,
      modulationIndex: 7,
      oscillator: { type: "triangle" },
      modulation: { type: "square" },
      envelope: { attack: 0.001, decay: 0.11, sustain: 0, release: 0.03 },
      modulationEnvelope: {
        attack: 0.001,
        decay: 0.08,
        sustain: 0,
        release: 0.02,
      },
    }).connect(this.launcherComp)

    this.fireShotSub = new Tone.MembraneSynth({
      pitchDecay: 0.035,
      octaves: 4,
      oscillator: { type: "sine" },
      envelope: { attack: 0.001, decay: 0.09, sustain: 0, release: 0.025 },
    }).connect(this.launcherComp)

    this.fireShotAir = new Tone.NoiseSynth({
      noise: { type: "pink" },
      envelope: { attack: 0.001, decay: 0.075, sustain: 0, release: 0.02 },
    }).connect(this.launcherComp)

    // ── GOO ──────────────────────────────────────────────────
    // Wet squelch: sine through resonant filter with rapid sweep.
    // Must be ultra-short for 120ms fire rate.
    this.gooFilter = new Tone.Filter({
      frequency: 1200,
      type: "bandpass",
      Q: 8,
    }).connect(this.launcherComp)

    this.gooSynth = new Tone.Synth({
      oscillator: { type: "sine" },
      envelope: { attack: 0.002, decay: 0.07, sustain: 0, release: 0.01 },
    }).connect(this.gooFilter)

    // ── ROAR ─────────────────────────────────────────────────
    // Aggressive: AM tremolo growl + deep sub drone.
    this.roarSynth = new Tone.AMSynth({
      harmonicity: 1.5,
      oscillator: { type: "sawtooth" },
      envelope: { attack: 0.03, decay: 0.25, sustain: 0.15, release: 0.12 },
      modulation: { type: "square" },
      modulationEnvelope: {
        attack: 0.02,
        decay: 0.15,
        sustain: 0.1,
        release: 0.06,
      },
    }).connect(this.launcherComp)

    this.roarSub = new Tone.Synth({
      oscillator: { type: "sine" },
      envelope: { attack: 0.02, decay: 0.3, sustain: 0.05, release: 0.08 },
    }).connect(this.launcherComp)

    // ── RICOCHET ─────────────────────────────────────────────
    // Metallic zing: sine with rapid upward pitch bend.
    this.ricochetSynth = new Tone.Synth({
      oscillator: { type: "sine" },
      envelope: { attack: 0.001, decay: 0.12, sustain: 0, release: 0.04 },
    }).connect(this.impactVerb)

    // ── MINE ─────────────────────────────────────────────────
    // Sharp metallic tick: short, precise, defined.
    this.mineSynth = new Tone.MetalSynth({
      envelope: { attack: 0.001, decay: 0.06, release: 0.01 },
      harmonicity: 5.1,
      modulationIndex: 20,
      resonance: 3000,
      octaves: 1,
    }).connect(this.launcherComp)

    // ── BEEP (gameplay) ──────────────────────────────────────
    this.beepSynth = new Tone.Synth({
      oscillator: { type: "triangle" },
      envelope: { attack: 0.001, decay: 0.06, sustain: 0, release: 0.02 },
    }).connect(this.uiFilter)

    // ── UI CLICK (menus, navigation) ─────────────────────────
    this.uiClickSynth = new Tone.Synth({
      oscillator: { type: "sine" },
      envelope: { attack: 0.001, decay: 0.05, sustain: 0, release: 0.015 },
    }).connect(this.uiFilter)

    this.rewardSynth = new Tone.PolySynth({
      voice: Tone.Synth,
      maxPolyphony: ToneSfxSynthesizer.rewardPolyphony,
      options: {
        oscillator: { type: "triangle" },
        envelope: { attack: 0.008, decay: 0.18, sustain: 0.12, release: 0.14 },
      },
    }).connect(this.uiBus)

    this.rewardBell = new Tone.MetalSynth({
      envelope: { attack: 0.001, decay: 0.14, release: 0.05 },
      harmonicity: 7.2,
      modulationIndex: 18,
      resonance: 3800,
      octaves: 1.8,
    }).connect(this.uiBus)

    // ── WAVE START ───────────────────────────────────────────
    // Triumphant brass stab: filtered sawtooth chord with swell.
    this.waveStartFilter = new Tone.Filter(2000, "lowpass").connect(
      this.ambientBus,
    )
    this.waveStartSynth = new Tone.PolySynth({
      voice: Tone.Synth,
      maxPolyphony: ToneSfxSynthesizer.waveStartPolyphony,
      options: {
        oscillator: { type: "sawtooth" },
        envelope: { attack: 0.08, decay: 0.3, sustain: 0.4, release: 0.5 },
      },
    }).connect(this.waveStartFilter)

    // ── WARP JUMP ────────────────────────────────────────────
    // Rising sweep: sawtooth building up + noise whoosh.
    this.warpSynth = new Tone.MonoSynth({
      oscillator: { type: "sawtooth" },
      envelope: { attack: 0.5, decay: 0.2, sustain: 0, release: 0.1 },
      filterEnvelope: {
        attack: 0.5,
        decay: 0.2,
        sustain: 0,
        release: 0.1,
        baseFrequency: 100,
        octaves: 5,
      },
    }).connect(this.ambientBus)

    this.warpNoise = new Tone.NoiseSynth({
      noise: { type: "white" },
      envelope: { attack: 0.4, decay: 0.3, sustain: 0, release: 0.1 },
    }).connect(this.ambientBus)

    // ── FLYBY ────────────────────────────────────────────────
    // Doppler: pitch rises then falls, panned L→R.
    this.flybyPan = new Tone.Panner(-1).connect(this.ambientBus)
    this.flybySynth = new Tone.Synth({
      oscillator: { type: "sawtooth" },
      envelope: { attack: 0.06, decay: 0.4, sustain: 0.1, release: 0.15 },
    }).connect(this.flybyPan)

    // ── THRUSTER ─────────────────────────────────────────────
    // Jet whoosh: pink noise through bandpass for engine character.
    this.thrusterBP = new Tone.Filter({
      frequency: 1000,
      type: "bandpass",
      Q: 2,
    }).connect(this.ambientBus)

    this.thrusterNoise = new Tone.NoiseSynth({
      noise: { type: "pink" },
      envelope: { attack: 0.04, decay: 0.15, sustain: 0.2, release: 0.15 },
    }).connect(this.thrusterBP)

    this.ballLaunchSynth = new Tone.MonoSynth({
      oscillator: { type: "square" },
      envelope: { attack: 0.001, decay: 0.08, sustain: 0, release: 0.03 },
      filterEnvelope: {
        attack: 0.001,
        decay: 0.06,
        sustain: 0,
        release: 0.02,
        baseFrequency: 700,
        octaves: 3.4,
      },
    }).connect(this.launcherComp)

    this.ballLaunchNoise = new Tone.NoiseSynth({
      noise: { type: "white" },
      envelope: { attack: 0.001, decay: 0.035, sustain: 0, release: 0.01 },
    }).connect(this.launcherComp)

    this.ballStateSynth = new Tone.FMSynth({
      harmonicity: 2.4,
      modulationIndex: 10,
      oscillator: { type: "triangle" },
      modulation: { type: "sine" },
      envelope: { attack: 0.002, decay: 0.16, sustain: 0, release: 0.03 },
      modulationEnvelope: {
        attack: 0.001,
        decay: 0.09,
        sustain: 0,
        release: 0.02,
      },
    }).connect(this.launcherComp)

    this.ballExpireNoise = new Tone.NoiseSynth({
      noise: { type: "pink" },
      envelope: { attack: 0.001, decay: 0.09, sustain: 0, release: 0.03 },
    }).connect(this.impactBus)

    this.initialized = true
  }

  // ─────────────────────────────────────────────────────────────
  //  HELPERS
  // ─────────────────────────────────────────────────────────────

  private rCents(range: number): number {
    return (Math.random() - 0.5) * range * 2
  }

  private rNote(notes: string[]): string {
    return notes[Math.floor(Math.random() * notes.length)]
  }

  private rFloat(min: number, max: number): number {
    return min + Math.random() * (max - min)
  }

  private accent(amount = 1): number {
    return Math.max(0.35, Math.min(1.75, amount))
  }

  private level(baseDb: number, amount = 1, spread = 8): number {
    return baseDb + (this.accent(amount) - 1) * spread
  }

  /** Current audio time — no added latency, safe() handles re-trigger collisions */
  private t(): number {
    return Tone.now()
  }

  /** Drop trigger if synth is busy */
  private safe(fn: () => void): void {
    SfxDebug.markTriggerCall()
    try {
      fn()
    } catch {
      /* synth busy — skip */
    }
    SfxDebug.markTriggerDone()
  }

  public setVolume(v: number): void {
    this.master.gain.rampTo(Math.max(0, Math.min(1, v)) * 0.55, 0.05)
  }

  /** Disconnect and dispose every Tone.js node to prevent audio leaks. */
  public dispose(): void {
    this.initialized = false
    // Dispose all synths/effects/buses. Order doesn't matter — dispose()
    // disconnects the node from the audio graph and frees WebAudio resources.
    const nodes: Tone.ToneAudioNode[] = [
      this.barkVocal,
      this.barkBody,
      this.laserSynth,
      this.bumperDing,
      this.bumperThump,
      this.exploBoom,
      this.exploKick,
      this.exploCrack,
      this.pickupSynth,
      this.damageCrush,
      this.damageNoise,
      this.damageThud,
      this.railSynth,
      this.shotgunPop,
      this.shotgunScatter,
      this.cannonBoom,
      this.cannonRumble,
      this.hadoukenCharge,
      this.hadoukenChargeNoise,
      this.hadoukenPulse,
      this.hadoukenSub,
      this.hadoukenAir,
      this.fireShotPulse,
      this.fireShotSub,
      this.fireShotAir,
      this.gooSynth,
      this.gooFilter,
      this.roarSynth,
      this.roarSub,
      this.ricochetSynth,
      this.mineSynth,
      this.beepSynth,
      this.uiClickSynth,
      this.rewardSynth,
      this.rewardBell,
      this.waveStartSynth,
      this.waveStartFilter,
      this.warpSynth,
      this.warpNoise,
      this.flybySynth,
      this.flybyPan,
      this.thrusterNoise,
      this.thrusterBP,
      this.ballLaunchSynth,
      this.ballLaunchNoise,
      this.ballStateSynth,
      this.ballExpireNoise,
      this.launcherComp,
      this.impactVerb,
      this.uiFilter,
      this.launcherBus,
      this.impactBus,
      this.uiBus,
      this.ambientBus,
      this.master,
    ]
    for (const n of nodes) {
      try {
        n.dispose()
      } catch {
        /* already disposed */
      }
    }
  }

  // ─────────────────────────────────────────────────────────────
  //  SOUND METHODS
  // ─────────────────────────────────────────────────────────────

  /** Bark: punchy "woof" with rapid formant-style pitch drop */
  public playBark(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.barkVocal.volume.value = this.level(-9, amount, 9)
      this.barkBody.volume.value = this.level(-12, amount, 7)
      // Vocal layer: start mid-high, sweep down fast for "woof" formant
      this.barkVocal.detune.value = this.rCents(40)
      this.barkVocal.triggerAttackRelease(
        this.rNote(["D3", "Eb3", "E3"]),
        0.1,
        t,
      )
      this.barkVocal.frequency.exponentialRampTo(80, 0.06, t)
      // Sub thump for chest resonance
      this.barkBody.triggerAttackRelease("G1", 0.08, t)
    })
  }

  /** Laser: classic descending "pew" with filter + pitch sweep */
  public playLaser(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.laserSynth.volume.value = this.level(-11, amount, 10)
      this.laserSynth.detune.value = this.rCents(80)
      // Start high, sweep down — the "pew" IS the pitch drop
      this.laserSynth.triggerAttackRelease(
        this.rNote(["E6", "F6", "G6"]),
        0.12,
        t,
      )
      this.laserSynth.frequency.exponentialRampTo(250, 0.1, t)
    })
  }

  /** Bumper: satisfying pinball "ding-thwack" */
  public playBumper(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      const a = this.accent(amount)
      const scale = ["C5", "D5", "E5", "F5", "G5", "A5"]
      const progress = Math.max(0, Math.min(1, amount - 1))
      const note = scale[Math.min(scale.length - 1, Math.floor(progress * 5))]
      this.bumperDing.volume.value = this.level(-12, a, 10)
      this.bumperThump.volume.value = this.level(-10, a, 8)
      this.ricochetSynth.volume.value = this.level(-20, a, 6)
      // Metallic ding climbs a C-major scale as the bumper chain nears overdrive.
      this.bumperDing.frequency.value = Tone.Frequency(note).toFrequency()
      this.bumperDing.triggerAttackRelease(0.15, t)
      // Sub thump for physicality
      this.bumperThump.triggerAttackRelease(
        this.rNote(["E2", "F2", "G2"]),
        0.1,
        t,
      )
      this.ricochetSynth.triggerAttackRelease(
        scale[Math.min(scale.length - 1, Math.floor(progress * 5))],
        0.05,
        t + 0.012,
      )
    })
  }

  /** Explosion: layered boom with crack + rumble + sub kick */
  public playExplosion(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.exploKick.volume.value = this.level(-7, amount, 10)
      this.exploCrack.volume.value = this.level(-12, amount, 8)
      this.exploBoom.volume.value = this.level(-11, amount, 10)
      // Sub kick — the "thud" you feel
      this.exploKick.triggerAttackRelease(this.rNote(["C1", "D1"]), 0.3, t)
      // White noise crack — the transient "snap" at the start
      this.exploCrack.triggerAttackRelease(0.04, t)
      // Brown noise body — the sustained rumble
      this.exploBoom.triggerAttackRelease(0.35, t + 0.005)
    })
  }

  /** Pickup: bright ascending 3-note arpeggio */
  public playPickup(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.pickupSynth.volume.value = this.level(-13, amount, 7)
      const root = Math.random() > 0.5 ? "C" : "A"
      const notes = root === "C" ? ["C5", "E5", "G5"] : ["A4", "C#5", "E5"]
      this.pickupSynth.triggerAttackRelease(notes[0], 0.12, t)
      this.pickupSynth.triggerAttackRelease(notes[1], 0.12, t + 0.06)
      this.pickupSynth.triggerAttackRelease(notes[2], 0.15, t + 0.12)
    })
  }

  /** Damage: crunchy crushed impact */
  public playDamage(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.damageNoise.volume.value = this.level(-9, amount, 10)
      this.damageThud.volume.value = this.level(-6, amount, 8)
      // Crushed noise burst for that "digital crunch"
      this.damageNoise.triggerAttackRelease(0.08, t)
      // Low thud for visceral impact
      this.damageThud.triggerAttackRelease("D1", 0.1, t)
    })
  }

  /** Wave start: triumphant brass chord with filter swell */
  public playWaveStart(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.waveStartSynth.volume.value = this.level(-16, amount, 6)
      // Filter opens during the swell for brass-like brightness
      this.waveStartFilter.frequency.setValueAtTime(600, t)
      this.waveStartFilter.frequency.exponentialRampTo(6000, 0.3, t)
      this.waveStartSynth.triggerAttackRelease(["C4", "E4", "G4", "C5"], 0.8, t)
    })
  }

  /** Plasma rail: high-energy FM zap with ascending sweep */
  public playPlasmaRail(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.railSynth.volume.value = this.level(-10, amount, 10)
      this.railSynth.detune.value = this.rCents(50)
      // Start low, sweep UP for "energy discharge" feel
      this.railSynth.triggerAttackRelease(this.rNote(["C4", "D4"]), 0.16, t)
      this.railSynth.frequency.exponentialRampTo(2400, 0.08, t)
    })
  }

  /** Shotgun: wide chunky blast */
  public playShotgun(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.shotgunPop.volume.value = this.level(-8, amount, 8)
      this.shotgunScatter.volume.value = this.level(-14, amount, 9)
      // Low pop for the initial "bang"
      this.shotgunPop.triggerAttackRelease(
        this.rNote(["C2", "D2", "E2"]),
        0.07,
        t,
      )
      // White noise scatter for the spread character
      this.shotgunScatter.triggerAttackRelease(0.12, t + 0.002)
    })
  }

  /** Heavy cannon: massive deep boom */
  public playHeavyCannon(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.cannonBoom.volume.value = this.level(-6, amount, 10)
      this.cannonRumble.volume.value = this.level(-12, amount, 8)
      // Deep kick with huge pitch sweep for room-shaking bass
      this.cannonBoom.triggerAttackRelease("C1", 0.3, t)
      // Noise rumble slightly delayed for body
      this.cannonRumble.triggerAttackRelease(0.2, t + 0.01)
    })
  }

  /** Hadouken charge: rising energy bloom before the boss releases the volley */
  public playHadoukenCharge(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.hadoukenCharge.volume.value = this.level(-17, amount, 7)
      this.hadoukenChargeNoise.volume.value = this.level(-25, amount, 6)
      this.hadoukenCharge.detune.value = this.rCents(30)
      this.hadoukenCharge.triggerAttackRelease("A2", 0.9, t)
      this.hadoukenCharge.frequency.exponentialRampTo(640, 0.78, t)
      this.hadoukenChargeNoise.triggerAttackRelease(0.82, t + 0.02)
    })
  }

  /** Hadouken release: three compact energy shoves matching the multi-projectile burst */
  public playHadouken(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      const accent = this.accent(amount)
      this.hadoukenPulse.volume.value = this.level(-8, accent, 8)
      this.hadoukenSub.volume.value = this.level(-9, accent, 8)
      this.hadoukenAir.volume.value = this.level(-16, accent, 7)
      this.hadoukenPulse.detune.value = this.rCents(35)

      const notes = ["A3", "C4", "E4"] as const
      for (let i = 0; i < notes.length; i += 1) {
        const at = t + i * 0.055
        this.hadoukenPulse.triggerAttackRelease(notes[i], 0.13, at)
        this.hadoukenSub.triggerAttackRelease(i === 0 ? "A1" : "C2", 0.1, at)
        this.hadoukenAir.triggerAttackRelease(0.08, at + 0.002)
      }
    })
  }

  /** Fire shot: compact hadouken-like punch for regular yellow fire projectiles */
  public playFireShot(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      const accent = this.accent(amount)
      this.fireShotPulse.volume.value = this.level(-5, accent, 8)
      this.fireShotSub.volume.value = this.level(-8, accent, 7)
      this.fireShotAir.volume.value = this.level(-13, accent, 7)
      this.fireShotPulse.detune.value = this.rCents(45)

      this.fireShotPulse.triggerAttackRelease(
        this.rNote(["G3", "A3", "C4"]),
        0.095,
        t,
      )
      this.fireShotSub.triggerAttackRelease("G1", 0.07, t)
      this.fireShotAir.triggerAttackRelease(0.055, t + 0.002)
    })
  }

  /** Goo: wet squelch with resonant filter sweep (ultra-short for 120ms fire rate) */
  public playGoo(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.gooSynth.volume.value = this.level(-10, amount, 8)
      // Resonant filter sweep creates the "wet" quality
      this.gooFilter.frequency.setValueAtTime(this.rFloat(1500, 3000), t)
      this.gooFilter.frequency.exponentialRampTo(300, 0.06, t)
      this.gooSynth.detune.value = this.rCents(120)
      this.gooSynth.triggerAttackRelease(
        this.rNote(["E3", "F3", "G3"]),
        0.06,
        t,
      )
    })
  }

  /** Roar: aggressive AM growl with sub drone */
  public playRoar(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.roarSynth.volume.value = this.level(-10, amount, 8)
      this.roarSub.volume.value = this.level(-14, amount, 6)
      this.roarSynth.triggerAttackRelease(this.rNote(["E2", "F2"]), 0.35, t)
      this.roarSub.triggerAttackRelease("C2", 0.3, t)
    })
  }

  /** Ricochet: metallic "zing" with rapid upward pitch bend */
  public playRicochet(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      const progress = Math.max(0, Math.min(1, amount - 1))
      const startNotes = ["C5", "D5", "E5", "F5", "G5", "A5"]
      const startNote =
        startNotes[Math.min(startNotes.length - 1, Math.floor(progress * 5))]
      const endFrequency = 1800 + progress * 1700
      this.ricochetSynth.volume.value = this.level(-12, amount, 10)
      // Start low, bend UP sharply for the "zing" character
      this.ricochetSynth.triggerAttackRelease(startNote, 0.12, t)
      this.ricochetSynth.frequency.exponentialRampTo(endFrequency, 0.05, t)
    })
  }

  /** Mine: sharp metallic tick */
  public playMine(amount = 1): void {
    this.safe(() => {
      this.mineSynth.volume.value = this.level(-13, amount, 7)
      this.mineSynth.triggerAttackRelease(0.05, this.t())
    })
  }

  /** Beep: clean game-UI tone */
  public playBeep(amount = 1): void {
    this.safe(() => {
      this.beepSynth.volume.value = this.level(-18, amount, 8)
      this.beepSynth.triggerAttackRelease(
        this.rNote(["C6", "D6"]),
        0.06,
        this.t(),
      )
    })
  }

  /** Thruster: filtered jet-engine burst */
  public playThruster(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.thrusterNoise.volume.value = this.level(-9, amount, 8)
      // Sweep bandpass frequency for "ignition" feel
      this.thrusterBP.frequency.setValueAtTime(420, t)
      this.thrusterBP.frequency.exponentialRampTo(2600, 0.12, t)
      this.thrusterNoise.triggerAttackRelease(0.16, t)
    })
  }

  /** Warp jump: rising sweep building to a whoosh */
  public playWarpJump(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.warpSynth.volume.value = this.level(-12, amount, 7)
      this.warpNoise.volume.value = this.level(-16, amount, 6)
      this.warpSynth.triggerAttackRelease("C6", 0.7, t)
      this.warpNoise.triggerAttackRelease(0.6, t + 0.1)
    })
  }

  /** Flyby: doppler effect with pitch rise→fall + L→R pan */
  public playFlyby(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.flybySynth.volume.value = this.level(-15, amount, 7)
      // Pan sweep L→R
      this.flybyPan.pan.setValueAtTime(-1, t)
      this.flybyPan.pan.linearRampTo(1, 0.5, t)
      // Pitch rises then falls for doppler
      this.flybySynth.triggerAttackRelease("G4", 0.5, t)
      this.flybySynth.frequency.setValueAtTime(300, t)
      this.flybySynth.frequency.exponentialRampTo(600, 0.2, t)
      this.flybySynth.frequency.exponentialRampTo(200, 0.3, t + 0.2)
    })
  }

  // ── UI sounds (menus) ──────────────────────────────────────

  /** UI beep: bright, quick confirmation */
  public playUiBeep(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.uiClickSynth.volume.value = this.level(-20, amount, 6)
      this.uiClickSynth.triggerAttackRelease("A5", 0.05, t)
      this.uiClickSynth.frequency.exponentialRampTo(1300, 0.03, t)
    })
  }

  /** UI press: deeper, satisfying button press */
  public playUiPress(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.uiClickSynth.volume.value = this.level(-16, amount, 7)
      this.uiClickSynth.triggerAttackRelease("E4", 0.05, t)
      this.uiClickSynth.frequency.exponentialRampTo(300, 0.03, t)
    })
  }

  /** UI navigate: subtle high tick for menu movement */
  public playUiNavigate(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.uiClickSynth.volume.value = this.level(-21, amount, 5)
      this.uiClickSynth.triggerAttackRelease("C6", 0.035, t)
      this.uiClickSynth.frequency.exponentialRampTo(1800, 0.02, t)
    })
  }

  public playBallLaunch(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.ballLaunchSynth.volume.value = this.level(-11, amount, 9)
      this.ballLaunchNoise.volume.value = this.level(-18, amount, 8)
      this.ballLaunchSynth.triggerAttackRelease(
        this.rNote(["G4", "A4", "B4"]),
        0.08,
        t,
      )
      this.ballLaunchSynth.frequency.exponentialRampTo(1400, 0.045, t)
      this.ballLaunchNoise.triggerAttackRelease(0.028, t)
    })
  }

  public playBallBounce(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.ricochetSynth.volume.value = this.level(-16, amount, 8)
      this.bumperThump.volume.value = this.level(-18, amount, 5)
      this.ricochetSynth.triggerAttackRelease(
        this.rNote(["B5", "D6", "F#6"]),
        0.06,
        t,
      )
      this.ricochetSynth.frequency.exponentialRampTo(2800, 0.03, t)
      this.bumperThump.triggerAttackRelease("A1", 0.045, t + 0.004)
    })
  }

  public playBallExpire(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.ballExpireNoise.volume.value = this.level(-18, amount, 8)
      this.beepSynth.volume.value = this.level(-22, amount, 6)
      this.ballExpireNoise.triggerAttackRelease(0.07, t)
      this.beepSynth.triggerAttackRelease("E5", 0.05, t)
      this.beepSynth.frequency.exponentialRampTo(320, 0.06, t)
    })
  }

  public playBallState(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.ballStateSynth.volume.value = this.level(-13, amount, 8)
      this.rewardBell.volume.value = this.level(-20, amount, 6)
      this.ballStateSynth.triggerAttackRelease(
        this.rNote(["A4", "C5", "E5"]),
        0.1,
        t,
      )
      this.ballStateSynth.frequency.exponentialRampTo(1800, 0.07, t)
      this.rewardBell.frequency.value = this.rFloat(900, 1500)
      this.rewardBell.triggerAttackRelease(0.08, t + 0.02)
    })
  }

  public playLevelUp(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.rewardSynth.volume.value = this.level(-15, amount, 8)
      this.rewardBell.volume.value = this.level(-16, amount, 8)
      this.rewardSynth.triggerAttackRelease(["C5", "E5", "G5"], 0.16, t)
      this.rewardSynth.triggerAttackRelease(["D5", "G5", "B5"], 0.18, t + 0.09)
      this.rewardBell.frequency.value = this.rFloat(1200, 1800)
      this.rewardBell.triggerAttackRelease(0.12, t + 0.06)
    })
  }

  public playUpgradePick(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.rewardSynth.volume.value = this.level(-17, amount, 7)
      this.uiClickSynth.volume.value = this.level(-18, amount, 6)
      this.rewardSynth.triggerAttackRelease(["G4", "B4", "D5"], 0.14, t)
      this.uiClickSynth.triggerAttackRelease("D6", 0.05, t + 0.03)
    })
  }

  public playShopOpen(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.rewardSynth.volume.value = this.level(-18, amount, 7)
      this.uiClickSynth.volume.value = this.level(-20, amount, 5)
      this.rewardSynth.triggerAttackRelease(["A4", "C5", "E5"], 0.12, t)
      this.uiClickSynth.triggerAttackRelease("E6", 0.04, t + 0.025)
      this.uiClickSynth.frequency.exponentialRampTo(900, 0.04, t + 0.025)
    })
  }

  public playComboTier(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.rewardBell.volume.value = this.level(-18, amount, 7)
      this.beepSynth.volume.value = this.level(-20, amount, 6)

      // Pitch shifts up heavily as combo tier increases (amount = tierBoost)
      const baseFreq = this.rFloat(1000, 1900)
      this.rewardBell.frequency.value = baseFreq * Math.max(1, amount * 1.2)
      this.rewardBell.triggerAttackRelease(0.09, t)

      const beepBaseFreq = 1568 // Approx G6
      this.beepSynth.frequency.value = beepBaseFreq * Math.max(1, amount * 1.2)
      this.beepSynth.triggerAttackRelease(0.045, t + 0.018)
    })
  }

  public playVictory(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.rewardSynth.volume.value = this.level(-13, amount, 8)
      this.rewardBell.volume.value = this.level(-15, amount, 7)
      this.rewardSynth.triggerAttackRelease(["C4", "E4", "G4", "C5"], 0.55, t)
      this.rewardSynth.triggerAttackRelease(
        ["D4", "G4", "B4", "D5"],
        0.6,
        t + 0.18,
      )
      this.rewardBell.frequency.value = this.rFloat(800, 1400)
      this.rewardBell.triggerAttackRelease(0.16, t + 0.12)
    })
  }

  public playGameOver(amount = 1): void {
    this.safe(() => {
      const t = this.t()
      this.rewardSynth.volume.value = this.level(-15, amount, 7)
      this.damageThud.volume.value = this.level(-12, amount, 7)
      this.rewardSynth.triggerAttackRelease(["E4", "C4", "A3"], 0.24, t)
      this.rewardSynth.triggerAttackRelease(["D4", "Bb3", "G3"], 0.28, t + 0.12)
      this.damageThud.triggerAttackRelease("C1", 0.14, t + 0.08)
    })
  }

  // ─────────────────────────────────────────────────────────────
  //  KEY DISPATCHER
  // ─────────────────────────────────────────────────────────────

  public play(key: string, amount = 1): void {
    if (!this.initialized) return
    SfxDebug.markDispatchEnter()

    switch (key) {
      case AUDIO.BARK:
        this.playBark(amount)
        break
      case AUDIO.LASER:
        this.playLaser(amount)
        break
      case AUDIO.BUMPER:
        this.playBumper(amount)
        break
      case AUDIO.EXPLOSION:
        this.playExplosion(amount)
        break
      case AUDIO.PICKUP:
        this.playPickup(amount)
        break
      case AUDIO.DAMAGE:
        this.playDamage(amount)
        break
      case AUDIO.WAVE_START:
        this.playWaveStart(amount)
        break
      case AUDIO.PLASMA_RAIL:
        this.playPlasmaRail(amount)
        break
      case AUDIO.SFX_RICOCHET:
        this.playRicochet(amount)
        break
      case AUDIO.SFX_SHOTGUN:
        this.playShotgun(amount)
        break
      case AUDIO.SFX_HEAVY_CANNON:
        this.playHeavyCannon(amount)
        break
      case AUDIO.SFX_HADOUKEN_CHARGE:
        this.playHadoukenCharge(amount)
        break
      case AUDIO.SFX_HADOUKEN:
        this.playHadouken(amount)
        break
      case AUDIO.SFX_FIRE_SHOT:
        this.playFireShot(amount)
        break
      case AUDIO.SFX_Goo:
        this.playGoo(amount)
        break
      case AUDIO.SFX_MINE:
        this.playMine(amount)
        break
      case AUDIO.SFX_ROAR:
        this.playRoar(amount)
        break
      case AUDIO.SFX_BEEP:
        this.playBeep(amount)
        break
      case AUDIO.SFX_THRUSTER:
        this.playThruster(amount)
        break
      case AUDIO.SFX_WARP_JUMP:
        this.playWarpJump(amount)
        break
      case AUDIO.SFX_FLYBY:
        this.playFlyby(amount)
        break
      case AUDIO.SFX_UI_BEEP:
        this.playUiBeep(amount)
        break
      case AUDIO.SFX_UI_PRESS:
        this.playUiPress(amount)
        break
      case AUDIO.SFX_UI_NAVIGATE:
        this.playUiNavigate(amount)
        break
      case AUDIO.BALL_LAUNCH:
        this.playBallLaunch(amount)
        break
      case AUDIO.BALL_BOUNCE:
        this.playBallBounce(amount)
        break
      case AUDIO.BALL_EXPIRE:
        this.playBallExpire(amount)
        break
      case AUDIO.SFX_BALL_STATE:
        this.playBallState(amount)
        break
      case AUDIO.SFX_LEVEL_UP:
        this.playLevelUp(amount)
        break
      case AUDIO.SFX_UPGRADE_PICK:
        this.playUpgradePick(amount)
        break
      case AUDIO.SFX_SHOP_OPEN:
        this.playShopOpen(amount)
        break
      case AUDIO.SFX_COMBO_TIER:
        this.playComboTier(amount)
        break
      case AUDIO.SFX_VICTORY:
        this.playVictory(amount)
        break
      case AUDIO.SFX_GAME_OVER:
        this.playGameOver(amount)
        break
    }
  }
}
